import { FileText } from "lucide-react";
import { ExpeditionDocumentFileDto } from "@/types/api/api-types";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { getFile } from "@/api/s3.api";
import { SingleDocumentUploadCard } from "@/components/backOffice/expeditionForm/singleDocumentUploadCard";
import { useCreateFile } from "@/api/file.api";
import { DeliveryFormOcrData } from "@/types/forms/home/cotationDeliveryForm.form";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  documents: ExpeditionDocumentFileDto[];
}

export const InfoInvoiceOcr = ({ documents }: Props) => {
  const { t } = useTranslation("boExpeditions");
  const methods = useFormContext<DeliveryFormOcrData>();
  const { mutateAsync: createFile, isPending } = useCreateFile();
  const [selectedFile, setSelectedFile] = useState<ExpeditionDocumentFileDto | null>(null);

  const invoice = documents.find((f) => f.type === FileTypeEnum.EXPEDITION_INVOICE);

  const handleOpen = async (path: string) => {
    const url = await getFile(path);
    window.open(url, "_blank");
  };

  const handleDownload = async (file: ExpeditionDocumentFileDto) => {
    const url = await getFile(file.path);
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  };

  const handleFileSelect = async (selectedFile: File) => {
    try {
      const { fileId, uploadUrl } = await createFile({
        fileName: selectedFile.name,
        type: FileTypeEnum.EXPEDITION_INVOICE,
        contentType: selectedFile.type,
      });

      await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: { "Content-Type": selectedFile.type },
      });

      methods.setValue("invoiceFileId", fileId);

      setSelectedFile({
        id: fileId,
        name: selectedFile.name,
        path: new URL(uploadUrl).pathname.slice(1),
        type: FileTypeEnum.EXPEDITION_INVOICE,
        createdAt: new Date().toISOString(),
      });

      toast.success(t("pendingShipmentsInvoiceOcr.uploadSuccess"));
    } catch {
      toast.error(t("pendingShipmentsInvoiceOcr.uploadError"));
    }
  };

  return (
    <div className="flex w-full flex-1 flex-col items-center gap-6">
      <div className="flex w-full max-w-[1000px] flex-col items-center justify-center gap-4 rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex flex-row items-start justify-start gap-4 self-start">
          <FileText />
          <h2 className="text-xm font-semibold text-black">
            {t("pendingShipmentsInvoiceOcr.title")}
          </h2>
        </div>
        <div className="flex max-h-[70vh] w-full flex-col gap-8 md:flex">
          <SingleDocumentUploadCard
            file={selectedFile || invoice}
            alternateFormats={undefined}
            emptyMessage={t("pendingShipmentsInvoiceOcr.empty")}
            uploadLabel={t("pendingShipmentsInvoiceOcr.uploadLabel")}
            accept=".pdf"
            isUploading={isPending}
            onOpen={handleOpen}
            onDownload={handleDownload}
            onFileSelect={handleFileSelect}
            hideDetailsInfo
          />
        </div>
      </div>
    </div>
  );
};
